import * as path from 'node:path';
import { cliSimpleGit } from '../../cli-simple-git.js';
import { getRemoteOriginUrl } from '../../remote-origin-url.js';
import { isGitRepoDirectory } from '../../is-git-repo.js';
import { yieldToEventLoop } from '../../git-runtime.js';
import { formatRepoShortTitle } from '../paths/repo-format.js';
import { listChangedFilesForCommit, listChangedFilesForRepo } from '../listing/index.js';
import type { WorkingTreeChangeRepoDetail } from '../types.js';
import type { WorkingTreeRepoChangesBasis } from './types.js';
import { loadRecentAndUnpushedCommitsForRepoDetail } from './load-recent-and-unpushed-commits-for-repo-detail.js';
import { loadWorkingTreeChangeRepoGitInfo } from './load-working-tree-change-repo-git-info.js';
import { resolveRepoPathRelativeToWorkspaceRoot } from './resolve-repo-path-relative-to-workspace-root.js';

/**
 * Build one repo's working-tree change detail, or `null` if the target is not a git repo or filtered out.
 */
export async function buildWorkingTreeChangeRepoDetail(options: {
  targetPath: string;
  bridgeRoot: string;
  sessionParentPath: string | null;
  repoFilterRelPath: string | null;
  basis: WorkingTreeRepoChangesBasis;
  recentCommitsLimit?: unknown;
  /** When false (default), file rows omit patch content until fetched per file. */
  includeFilePatches?: boolean;
}): Promise<WorkingTreeChangeRepoDetail | null> {
  const checkoutPath = path.resolve(options.targetPath);
  if (!(await isGitRepoDirectory(checkoutPath))) return null;

  const isCommitView = options.basis.kind === 'commit';
  const sessionParentPath = options.sessionParentPath ? path.resolve(options.sessionParentPath) : null;
  const git = cliSimpleGit(checkoutPath);

  const repoPathRelativeToWorkspaceRoot = await resolveRepoPathRelativeToWorkspaceRoot({
    checkoutPath,
    bridgeRoot: options.bridgeRoot,
    sessionParentPath,
  });
  if (!repoPathRelativeToWorkspaceRoot) return null;
  if (options.repoFilterRelPath && repoPathRelativeToWorkspaceRoot !== options.repoFilterRelPath) {
    return null;
  }

  const { branch, remoteUrl, remoteDisplay } = await loadWorkingTreeChangeRepoGitInfo(checkoutPath);

  const repoDisplayName = formatRepoShortTitle(
    remoteUrl,
    repoPathRelativeToWorkspaceRoot === '.' ? '.' : repoPathRelativeToWorkspaceRoot,
  );
  const repoPathForFileListing =
    repoPathRelativeToWorkspaceRoot === '.' ? '.' : repoPathRelativeToWorkspaceRoot;
  const commitSha = isCommitView ? options.basis.sha.trim() : null;

  const filesPromise =
    isCommitView && commitSha
      ? listChangedFilesForCommit(checkoutPath, repoPathForFileListing, commitSha, {
          includeFilePatches: options.includeFilePatches,
        })
      : listChangedFilesForRepo(checkoutPath, repoPathForFileListing, {
          includeFilePatches: options.includeFilePatches,
        });

  const statusPromise = isCommitView
    ? Promise.resolve(false)
    : git.status().then((st) => (st.files?.length ?? 0) > 0);

  const commitsPromise = isCommitView
    ? Promise.resolve(null)
    : loadRecentAndUnpushedCommitsForRepoDetail(checkoutPath, options.recentCommitsLimit);

  const [files, hasUncommittedChanges, commitData] = await Promise.all([
    filesPromise,
    statusPromise,
    commitsPromise,
  ]);

  return {
    repoRelPath: repoPathRelativeToWorkspaceRoot,
    repoDisplayName,
    branch,
    remoteUrl,
    remoteDisplay,
    files,
    hasUncommittedChanges,
    unpushedCommits: commitData?.unpushedCommits ?? [],
    recentCommits: commitData?.recentCommits ?? [],
    recentCommitsHasMore: commitData?.recentCommitsHasMore ?? false,
    changesView: isCommitView ? 'commit' : 'working',
    changesCommitSha: commitSha,
  };
}
